import { BehaviorSubject, filter, map, Observable, Subject, take } from 'rxjs';
import { inject, Injectable } from '@angular/core';
import { APIService, DomainValue } from '../../services/api.service';
import { StateService } from '../../services/state.service';
import { CurrentProposition } from './maps-proposition/map-proposition.interface';
import { environment } from '../../../environments/environment';
import { Coordinate } from 'ol/coordinate';
import { Feature } from 'ol';
import { GeoJSON } from 'ol/format';
import { LoggingService } from '../../services/logging.service';
import { NzMessageService } from 'ng-zorro-antd/message';
import { DataService } from '../offline/data-service';
import { AmplifyApiService } from 'src/app/services/amplify-api.service';

interface PlotAndPlantationDate {
  plotFid: string;
  plantationDate: Date | string;
}

interface PlotAndSystem {
  plotFid: string;
  system: any;
}

export interface PlotAndTreatmentType {
  plotFid: string;
  treatmentTypeCode: string;
}

export interface PlotAndExploitationType {
  plotFid: string;
  exploitationType: string;
}

export interface PropertyAndCommitmentLevel {
  propertyFid: string;
  commitmentLevelCode: string;
}

export interface ProgramGIS {
  baseSchema: string;
  draftSchema: string;
  geoserverWorkspace: string;
  id: string;
  name: string;
}

export enum MapAction {
  drawShape,
  cancelPolygon,
  confirmPolygonCreation,
  editPolygon,
  editLine,
  editCircle,
  confirmLineCreation,
  confirmCircleCreation,
  deleteCircle,
  deleteLine,
  deletePolygon,
  confirmPolygonEdition,
  confirmCircleEdition,
  confirmLineEdition,
  editMapProperties,
  drawPolygon,
  drawMarker,
  cancelMarker,
  confirmMarkerCreation,
  editMarker,
  deleteMarker,
  confirmMarkerEdition,
  editShapeProperties,
  openFormAssociationsModal,
  importFile,
  drawCircle,
  drawLine,
  cancelLine,
  cancelCircle,
  addPointFromCurrentLocation,
  changeMarkerProperties,
  changeLineProperties,
  changePolygonProperties,
  changeGeolocProperties,
  sendMapProposition,
  drawEnd,
  addPolygonPart,
  addLinePart,
}

@Injectable({
  providedIn: 'root',
})
export class MapsService {
  public loadingNearbyMaps = false;
  private readonly overviewMapSettingsLocalStorageKey = 'overviewMapSettings';
  private elevationService: any;
  public fetchingShapes = false;
  public centerLat: number | undefined = undefined;
  public centerLng: number | undefined = undefined;
  public zoom: number | undefined = undefined;

  public additionalLayersPerWorkspace: { [index: string]: string[] } = {
    base_map: ['ZP_TEX_ESPECIES', 'PALMA_2023', 'Cultivo_Arroz_2023'],
    di_base_map: [],
    eleonore_base_map: [],
    re_base_map: [],
  };

  public static readonly propositionsStatuses = {
    new: {
      label: 'New',
      label_fr: 'Nouveau',
      label_es: 'Nuevo',
    },
  };

  public static readonly allFeatureNames = (geoserverWorkspace: string | undefined): string[] => {
    return [
      `${geoserverWorkspace}:intervention_area_with_program`,
      `${geoserverWorkspace}:plot_with_program`,
      `${geoserverWorkspace}:reference_plot_with_program`,
      `${geoserverWorkspace}:property_with_program`,
      `${geoserverWorkspace}:station_with_program`,
      `${geoserverWorkspace}:program_with_program`,
      `${geoserverWorkspace}:zone_with_program`,
      `${geoserverWorkspace}:ucs_with_program`,
    ];
  };

  public static readonly propertyAndPlotFeatureNames = (geoserverWorkspace: string | undefined): string[] => {
    return [
      `${geoserverWorkspace}:plot_with_program`,
      `${geoserverWorkspace}:reference_plot_with_program`,
      `${geoserverWorkspace}:property_with_program`,
      `${geoserverWorkspace}:program_with_program`,
    ];
  };

  public static readonly propertyAndProgramFeatureNames = (geoserverWorkspace: string | undefined): string[] => {
    return [`${geoserverWorkspace}:property_with_program`, `${geoserverWorkspace}:program_with_program`];
  };

  public static coordinateProjections = {
    web: 'EPSG:3857',
    lonLat: 'EPSG:4326',
  };

  constructor(
    private readonly stateService: StateService,
    private readonly logger: LoggingService,
    private readonly apiService: APIService,
    private readonly msg: NzMessageService,
    private readonly dataService: DataService,
  ) {
    try {
      this.elevationService = new google.maps.ElevationService();
    } catch (error) {
      this.logger.error(error, false);
    }
    setInterval(() => {
      if (!document.hidden) {
        this.refreshCurrentProgramMapFeatures();
      }
    }, 60000);
  }

  private readonly API = inject(AmplifyApiService);
  public static shapeTypesThatAreMarker = ['point'];
  public static shapeTypesThatAreLine = ['accessPath', 'line', 'road', 'stream'];

  private readonly shapeClickedSubject = new Subject<Feature>();
  private readonly geoLocationChangedSubject = new BehaviorSubject<Coordinate>([0, 0]);
  private readonly mapActionTriggeredSubject = new Subject<[MapAction, any]>();
  private readonly propositionSavedSubject = new Subject<CurrentProposition>();
  private readonly propositionChangedSubject = new Subject<CurrentProposition>();

  public allCurrentProgramMapFeatures: Feature[] = [];
  private currentProgramLastModified: Date | null = null;
  public currentProgramMapId = '';
  public currentGeoserverWorkspace = '';

  public convertToHa(meterSquares: number): number {
    return meterSquares * 0.0001;
  }

  public getMapShapeClickedObservable(): Observable<Feature> {
    return this.shapeClickedSubject.asObservable();
  }

  public triggerShapeClicked(feature: Feature): void {
    this.shapeClickedSubject.next(feature);
  }

  public triggerMapAction(action: MapAction, actionObject: any = undefined): void {
    this.mapActionTriggeredSubject.next([action, actionObject]);
  }

  public emitGeolocationCoordinates(coords: Coordinate): void {
    this.geoLocationChangedSubject.next(coords);
  }

  public getGeoLocationChangedObservable(): Observable<Coordinate> {
    return this.geoLocationChangedSubject.asObservable();
  }

  public getMapActionChangedObservable(): Observable<[MapAction, any]> {
    return this.mapActionTriggeredSubject.asObservable();
  }

  public getPropositionSavedObservable(): Observable<CurrentProposition> {
    return this.propositionSavedSubject.asObservable();
  }

  public getPropositionChangedObservable(): Observable<CurrentProposition> {
    return this.propositionChangedSubject.asObservable();
  }

  public emitPropositionChanged(proposition: CurrentProposition): void {
    this.propositionChangedSubject.next(proposition);
  }

  public getShapeTypesObservable(): Observable<DomainValue[]> {
    return this.stateService.getDomainValueObs('shapeType').pipe(
      filter((shapeTypes) => shapeTypes.name === 'shapeType' && shapeTypes.domainValues.length > 0),
      take(1),
      map((shapeType: { name: string; domainValues: DomainValue[] }) => {
        return shapeType.domainValues;
      }),
    );
  }

  public async sendMapProposition(proposition: CurrentProposition): Promise<any> {
    return this.API.post(environment.API_NAME, `/maps/gis/proposition`, {
      body: proposition,
    });
  }

  public async getFeatureMetadata(feature: Feature): Promise<any> {
    return this.API.post(environment.API_NAME, `/maps/gis/fetch-metadata`, {
      body: new GeoJSON().writeFeatureObject(feature, {
        featureProjection: MapsService.coordinateProjections.web,
        dataProjection: MapsService.coordinateProjections.lonLat,
      }),
    });
  }

  public async assignPlotsSystems(plotsAndSystems: PlotAndSystem[], geoserverWorkspace: string): Promise<any> {
    return this.API.put(environment.API_NAME, `/maps/gis/plot/system`, {
      body: { plotsAndSystems, geoserverWorkspace },
    });
  }

  public async assignPlotsPlantationDates(
    plotsAndPlantationDates: PlotAndPlantationDate[],
    geoserverWorkspace: string,
  ): Promise<any> {
    return this.API.put(environment.API_NAME, `/maps/gis/plot/plantation-date`, {
      body: { plotsAndPlantationDates, geoserverWorkspace },
    });
  }

  public async assignPlotsTreatmentTypes(
    plotsAndTreatmentTypes: PlotAndTreatmentType[],
    geoserverWorkspace: string,
  ): Promise<any> {
    return this.API.put(environment.API_NAME, `/maps/gis/plot/treatment-type`, {
      body: { plotsAndTreatmentTypes, geoserverWorkspace },
    });
  }

  public async assignPlotsExploitationTypes(
    plotsAndExploitationTypes: PlotAndExploitationType[],
    geoserverWorkspace: string,
  ): Promise<any> {
    return this.API.put(environment.API_NAME, `/maps/gis/plot/exploitation-type`, {
      body: { plotsAndExploitationTypes, geoserverWorkspace },
    });
  }

  public async assignPropertiesCommitmentLevels(
    propertiesAndCommitmentLevel: PropertyAndCommitmentLevel[],
    geoserverWorkspace: string,
  ): Promise<any> {
    return this.API.put(environment.API_NAME, `/maps/gis/property/commitment-level`, {
      body: { propertiesAndCommitmentLevel, geoserverWorkspace },
    });
  }

  public setShapeTags(params: { shapeId: string; geoserverWorkspace: string; tags: string[] }): Promise<any> {
    return this.API.post(environment.API_NAME, `/maps/gis/shape/${params.shapeId}/tags`, {
      body: {
        tags: params.tags,
        geoserverWorkspace: params.geoserverWorkspace,
      },
    });
  }

  public insertShapeTags(params: { shapeId: string; geoserverWorkspace: string; tags: string[] }): Promise<any> {
    return this.API.put(environment.API_NAME, `/maps/gis/shape/${params.shapeId}/tags`, {
      body: {
        tags: params.tags,
        geoserverWorkspace: params.geoserverWorkspace,
      },
    });
  }

  public async getAllPrograms(): Promise<ProgramGIS[]> {
    return this.API.get(environment.API_NAME, '/maps/gis/program', {});
  }

  public async saveLocalMapProposition(proposition: CurrentProposition): Promise<void> {
    if (this.apiService.onLine) {
      const postedProposition = await this.apiService.postLocalProposition(proposition);
      proposition.timestamp = postedProposition.timestamp;
      proposition.inSync = true;
      proposition.savedToDb = true;
    } else {
      proposition.inSync = false;
    }

    await this.dataService.saveMapProposition({
      id: proposition.id,
      timestamp: proposition.timestamp,
      content: proposition,
    });
  }

  public async getLocalMapProposition(propositionId: string, geoserverWorskapce: string): Promise<any> {
    const localProposition: CurrentProposition = this.apiService.onLine
      ? await this.apiService.getLocalProposition(propositionId)
      : (await this.dataService.getMapProposition(propositionId))?.content;

    if (localProposition) {
      const propositionsFromBaseMap = await this.getFromBaseMap({
        featureId: propositionId,
        typeNames: [`${geoserverWorskapce}:propositions_comments`],
        geoserverWorkspace: geoserverWorskapce,
      });

      const featuresFromBaseMap = new GeoJSON().readFeatures(propositionsFromBaseMap);

      [...localProposition.polygons, ...localProposition.lines, ...localProposition.markers].forEach((localFeature) => {
        const matchingFeatureFromDb = featuresFromBaseMap.find(
          (feature) => feature.get('shape_id') === localFeature.id,
        );

        if (matchingFeatureFromDb && matchingFeatureFromDb.get('comments')) {
          localFeature.properties.notes = matchingFeatureFromDb.get('comments');
        }
      });
    }

    return localProposition;
  }

  public async getLocalMapPropositions(fromIndexDb = false, refreshIndexDb = false): Promise<CurrentProposition[]> {
    if (this.apiService.onLine && !fromIndexDb) {
      const propositions = await this.apiService.getLocalPropositions();

      if (refreshIndexDb) {
        const promisesToResolve = propositions.map((prop) =>
          this.dataService.saveMapProposition({
            id: prop.id,
            timestamp: prop.timestamp,
            content: prop,
          }),
        );

        await Promise.all(promisesToResolve);
      }

      return propositions;
    }

    return (await this.dataService.getMapPropositions()).map((item: { content: any }) => item.content);
  }

  public async getMyPrograms(): Promise<any> {
    if (this.apiService.onLine) {
      const result = await this.API.get(environment.API_NAME, '/maps/gis/program/me', {});
      this.dataService.populateMyPrograms(result);
      return result;
    } else {
      const offlineResult = await this.dataService.getMyPrograms();
      if (!offlineResult) {
        this.logger.error(new Error('Offline getMyPrograms did not return one element'), false);
      }
      return offlineResult;
    }
  }

  public async getFromBaseMap(
    params: {
      extent?: number[];
      typeNames?: string[];
      crs?: string;
      programIds?: string[];
      programId?: string;
      featureId?: string;
      hideGeometry?: boolean;
      geoserverWorkspace?: string;
    },
    pageSize: number = 100,
  ): Promise<any> {
    if (!params) {
      throw new Error('params mandatory for getFromBaseMap');
    }
    if (params.typeNames && params.typeNames.length) {
      let features = await Promise.all(
        params.typeNames.map((typeName) => this.callGetFromBaseMap({ ...params, typeNames: [typeName] }, pageSize)),
      );

      features = features.reduce((previousFeatures, currentFeatures) => {
        if (!previousFeatures) {
          return currentFeatures;
        } else {
          previousFeatures.features.push(...(currentFeatures?.features || []));
          previousFeatures.numberReturned += currentFeatures?.numberReturned;
          return previousFeatures;
        }
      });

      return features;
    } else {
      return this.callGetFromBaseMap(params, pageSize);
    }
  }

  private async callGetFromBaseMap(
    params: {
      extent?: number[];
      typeNames?: string[];
      crs?: string;
      programIds?: string[];
      programId?: string;
      featureId?: string;
      hideGeometry?: boolean;
      geoserverWorkspace?: string;
    },
    pageSize: number = 100,
  ): Promise<any> {
    if (!params) {
      throw new Error('params mandatory for getFromBaseMap');
    }
    const urlToFetch = new URL(`http://hostname/maps/gis/basemap`);

    const bbox: any[] = [...(params.extent || [])];

    if (params.crs) {
      bbox.push(params.crs);
    }

    if (bbox?.length > 1) {
      urlToFetch.searchParams.append('bbox', bbox.join(','));
    }

    if (params.typeNames) {
      urlToFetch.searchParams.append('typenames', params.typeNames.join(','));
    }

    if (params.programId) {
      urlToFetch.searchParams.append('programid', params.programId);
    }

    if (params.programIds) {
      urlToFetch.searchParams.append('programids', params.programIds.join(','));
    }

    if (params.featureId) {
      urlToFetch.searchParams.append('featureid', params.featureId);
    }

    if (params.hideGeometry !== undefined) {
      urlToFetch.searchParams.append('hideGeometry', params.hideGeometry.toString());
    }

    if (params.geoserverWorkspace) {
      urlToFetch.searchParams.append('geoserverWorkspace', params.geoserverWorkspace);
    }

    if (this.apiService.onLine) {
      let startIndex = 0;
      urlToFetch.searchParams.set('startIndex', startIndex.toString());
      urlToFetch.searchParams.set('count', pageSize.toString());
      const result = await this.API.get(environment.API_NAME, `${urlToFetch.pathname}${urlToFetch.search}`, {});
      let lastResult = result;

      while (lastResult?.features?.length === pageSize) {
        startIndex = startIndex + pageSize;
        urlToFetch.searchParams.set('startIndex', startIndex.toString());
        lastResult = await this.API.get(environment.API_NAME, `${urlToFetch.pathname}${urlToFetch.search}`, {});
        result.features.push(...(lastResult?.features || []));
        result.numberReturned += lastResult?.numberReturned;
      }

      this.dataService.populateProgramMapFeatures(JSON.stringify(params), result);
      return result;
    } else {
      const offlineResult = await this.dataService.getProgramMapFeatures(JSON.stringify(params));
      if (offlineResult.features.length === 0) {
        this.logger.error(
          new Error('Offline getProgramMapFeatures returned no result for ' + JSON.stringify(params)),
          false,
        );
      }
      return offlineResult;
    }
  }

  public async fetchCurrentProgramMapFeatures(
    programMapId: string | undefined,
    geoserverWorkspace: string | undefined,
    force = false,
  ): Promise<void> {
    if (!programMapId) {
      throw new Error('programMapId mandatory');
    }
    if (!geoserverWorkspace) {
      throw new Error('geoserverWorkspace mandatory');
    }
    if (this.fetchingShapes) {
      if (!force) {
        this.logger.warn('Currently fetching shapes, returning');
        return;
      }
    }
    try {
      this.fetchingShapes = true;
      const fetchedFeatureCollection = await this.getFromBaseMap({
        typeNames: MapsService.allFeatureNames(geoserverWorkspace),
        programId: programMapId,
        hideGeometry: true,
        geoserverWorkspace,
      });
      this.currentProgramLastModified = await this.getProgramLastModified(programMapId, geoserverWorkspace);
      this.currentProgramMapId = programMapId;
      this.currentGeoserverWorkspace = geoserverWorkspace;
      this.allCurrentProgramMapFeatures = new GeoJSON().readFeatures(fetchedFeatureCollection, {
        featureProjection: MapsService.coordinateProjections.web,
      });
      this.logger.warn('Fetched program features', this.allCurrentProgramMapFeatures.length);
    } catch (error) {
      this.logger.error(error, false);
    } finally {
      this.fetchingShapes = false;
    }
  }

  private refreshCurrentProgramMapFeatures(): void {
    if (this.currentProgramMapId) {
      if (this.allCurrentProgramMapFeatures.length === 0 || this.currentProgramLastModified === null) {
        this.fetchCurrentProgramMapFeatures(this.currentProgramMapId, this.currentGeoserverWorkspace)
          .then()
          .catch((error) => {
            this.logger.error(error, false);
          });
      } else {
        this.getProgramLastModified(this.currentProgramMapId, this.currentGeoserverWorkspace)
          .then((lastModified) => {
            if (!lastModified || lastModified.getTime() > (this.currentProgramLastModified?.getTime() || 0)) {
              this.fetchCurrentProgramMapFeatures(this.currentProgramMapId, this.currentGeoserverWorkspace)
                .then()
                .catch((error) => {
                  this.logger.error(error, false);
                });
            }
            this.currentProgramLastModified = lastModified;
          })
          .catch((error) => {
            this.logger.error(error, false);
          });
      }
    }
  }

  public getOverviewMapSettings(): any {
    return localStorage.getItem(this.overviewMapSettingsLocalStorageKey)
      ? JSON.parse(localStorage.getItem(this.overviewMapSettingsLocalStorageKey) as string)
      : null;
  }

  public setOverviewMapSettings(settings: { center: google.maps.LatLngLiteral; zoom: number }): void {
    localStorage.setItem(this.overviewMapSettingsLocalStorageKey, JSON.stringify(settings));
  }

  public getFreshShapeFeaturesFromObjField(formField: string, programId: string, formContent: any = null): Feature[] {
    const featuresToReturn: Feature[] = [];

    const formFieldValue = formContent[formField];

    if (formFieldValue && Array.isArray(formFieldValue.features)) {
      if (this.allCurrentProgramMapFeatures.length === 0 || this.fetchingShapes) {
        formFieldValue.features.forEach((fieldFeature: any) => {
          featuresToReturn.push(new GeoJSON().readFeatures(fieldFeature)[0]);
        });
      } else {
        formFieldValue.features.forEach((fieldFeature: any) => {
          const freshFeatureFound = this.allCurrentProgramMapFeatures.find(
            (freshFeature) => freshFeature.getId() === fieldFeature.id,
          );

          if (freshFeatureFound) {
            featuresToReturn.push(freshFeatureFound);
          } else {
            const error = new Error(
              $localize`A linked polygon has been deleted from the Database. Please reassign field with a new polygon: ` +
                ' ' +
                formField,
            );
            this.logger.error(error, false);
            this.msg.warning(
              $localize`A linked polygon has been deleted from the Database. Please reassign field with a new polygon: ` +
                ' ' +
                formField,
              {
                nzDuration: 12000,
              },
            );
          }
        });
      }
    }

    return featuresToReturn;
  }

  public getChildrenFeaturesOfParent(parentFeature: Feature, childType: string, childParentKeyId: string): Feature[] {
    return this.allCurrentProgramMapFeatures.filter(
      (freshFeature) =>
        freshFeature.get('type') === childType && freshFeature.get(childParentKeyId) === parentFeature.get('id'),
    );
  }

  public async getElevationInfosOf(lat: number, lng: number): Promise<number> {
    // Ignore typescript definitions because of conflict between googlemaps and google.maps modules
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    if (!this.elevationService) {
      this.elevationService = new google.maps.ElevationService();
    }
    const elevationResponse: any = await this.elevationService.getElevationForLocations({
      locations: [{ lat, lng }],
    });

    return elevationResponse?.results?.[0]?.elevation;
  }

  public async getProgramLastModified(programMapId: string, geoserverWorkspace: string): Promise<Date | null> {
    if (this.apiService.onLine) {
      const response = await this.getFromBaseMap({
        typeNames: [`${geoserverWorkspace}:program_last_modified`],
        featureId: programMapId,
        geoserverWorkspace,
      });

      if (response.features && response.features[0]?.properties.last_modified) {
        return new Date(response.features[0]?.properties.last_modified);
      }
      return null;
    } else {
      return new Date();
    }
  }
}
